/**
 * Configuration Stripe pour GetProof
 * 
 * Ce fichier documente tous les produits et prix Stripe configurés dans le dashboard.
 * Les IDs de prix sont utilisés pour créer des sessions de checkout Stripe.
 * 
 * IMPORTANT: Les métadonnées credit_amount doivent être ajoutées à chaque prix dans le dashboard Stripe
 * pour permettre au webhook de valider les paiements correctement.
 * 
 * Requirements: 4.4, 5.2
 * 
 * Dernière mise à jour: 2025-11-28
 */

export const STRIPE_CONFIG = {
  // ============================================================================
  // PRODUITS RÉCURRENTS (Abonnements)
  // ============================================================================
  
  branding_pro: {
    product_id: 'prod_TLQs6IbAcahQYd',
    price_id: 'price_1SOkUcEJERPAjYXBaCbAHUZ0',
    price: 30.00,
    currency: 'EUR',
    interval: 'month',
    description: 'Option marque blanche'
  },
  
  seat_additional: {
    product_id: 'prod_TLQtYMBnrwyl12',
    price_id: 'price_1SOkZFEJERPAjYXB02kYxc2r',
    price: 19.00,
    currency: 'EUR',
    interval: 'month',
    description: 'Licence recruteur supplémentaire'
  },

  // ============================================================================
  // PACKS DE CRÉDITS PRÉDÉFINIS
  // ============================================================================
  // Chaque pack a un prix fixe correspondant au palier dégressif
  // Format: { credits, product_id, price_id, unit_price }
  
  credit_packs: [
    {
      credits: 1,
      product_id: 'prod_TLQt64MyqC89tI',
      price_id: 'price_1SOk1kEJERPAjYXB4KFCnoMB',
      unit_price: 15.00,
      total_price: 15.00,
      currency: 'EUR'
    },
    {
      credits: 5,
      product_id: 'prod_TLTRVgJnRllS23',
      price_id: 'price_1SOmUREJERPAjYXBMvMvQrbl',
      unit_price: 14.00,
      total_price: 70.00,
      currency: 'EUR'
    },
    {
      credits: 10,
      product_id: 'prod_TLQtHFGEH9K6q4',
      price_id: 'price_1SOk21EJERPAjYXBgWreM8rz',
      unit_price: 14.00,
      total_price: 140.00,
      currency: 'EUR'
    },
    {
      credits: 20,
      product_id: 'prod_TLQtfIs6GSuaET',
      price_id: 'price_1SOk2HEJERPAjYXBUkt6cGzf',
      unit_price: 13.00,
      total_price: 260.00,
      currency: 'EUR'
    },
    {
      credits: 30,
      product_id: 'prod_TLQuJgeSZEzoT8',
      price_id: 'price_1SOk2ZEJERPAjYXBfV97S1iR',
      unit_price: 12.00,
      total_price: 360.00,
      currency: 'EUR'
    },
    {
      credits: 40,
      product_id: 'prod_TLQuCIhqpoZsEi',
      price_id: 'price_1SOk2oEJERPAjYXB2NA98TIr',
      unit_price: 11.00,
      total_price: 440.00,
      currency: 'EUR'
    },
    {
      credits: 50,
      product_id: 'prod_TLQuvhZT7jNnuG',
      price_id: 'price_1SOk34EJERPAjYXB5pOaIdMm',
      unit_price: 10.00,
      total_price: 500.00,
      currency: 'EUR'
    },
    {
      credits: 100,
      product_id: 'prod_TLQvUnZnoh0oJt',
      price_id: 'price_1SOk3LEJERPAjYXB1r3fk1ey',
      unit_price: 9.00,
      total_price: 900.00,
      currency: 'EUR'
    },
    {
      credits: 200,
      product_id: 'prod_TLQvbunpgQopr5',
      price_id: 'price_1SOk3gEJERPAjYXBiYx6l8rE',
      unit_price: 8.00,
      total_price: 1600.00,
      currency: 'EUR'
    },
    {
      credits: 500,
      product_id: 'prod_TLQvNyb7GXNsbZ',
      price_id: 'price_1SOk3xEJERPAjYXBaJPNspX5',
      unit_price: 7.00,
      total_price: 3500.00,
      currency: 'EUR'
    }
  ],

  // ============================================================================
  // PALIERS DE PRIX DÉGRESSIFS
  // ============================================================================
  // Utilisés pour calculer le prix de montants personnalisés (1-10000 crédits)
  
  credit_tiers: [
    { min: 1, max: 9, unit_price: 15.00 },
    { min: 10, max: 19, unit_price: 14.00 },
    { min: 20, max: 29, unit_price: 13.00 },
    { min: 30, max: 39, unit_price: 12.00 },
    { min: 40, max: 49, unit_price: 11.00 },
    { min: 50, max: 99, unit_price: 10.00 },
    { min: 100, max: 199, unit_price: 9.00 },
    { min: 200, max: 499, unit_price: 8.00 },
    { min: 500, max: Infinity, unit_price: 7.00 }
  ],

  // ============================================================================
  // PRODUIT GÉNÉRIQUE POUR MONTANTS PERSONNALISÉS
  // ============================================================================
  // Utilisé pour créer des sessions Stripe avec des quantités variables
  
  credits_product: {
    product_id: 'prod_TLQt64MyqC89tI',
    price_id: 'price_1SOk1kEJERPAjYXB4KFCnoMB',
    currency: 'EUR',
    description: '1 crédit = 1 prise de référence complète'
  }
} as const;

// ============================================================================
// TYPES TYPESCRIPT
// ============================================================================

/**
 * Pack de crédits prédéfini avec prix fixe
 */
export interface CreditPack {
  credits: number;
  product_id: string;
  price_id: string;
  unit_price: number;
  total_price: number;
  currency: string;
}

/**
 * Produit d'abonnement récurrent
 */
export interface SubscriptionProduct {
  product_id: string;
  price_id: string;
  price: number;
  currency: string;
  interval: 'month' | 'year';
  description: string;
}

/**
 * Palier de prix dégressif
 */
export interface CreditTier {
  min: number;
  max: number;
  unit_price: number;
}

/**
 * Résultat du calcul de prix
 */
export interface PriceCalculation {
  total: number;
  unit_price: number;
  savings: number;
}

// ============================================================================
// FONCTIONS UTILITAIRES
// ============================================================================

/**
 * Récupère le prix unitaire pour une quantité donnée selon les paliers dégressifs
 * 
 * @param quantity - Nombre de crédits
 * @returns Prix unitaire en euros
 */
export const getCreditUnitPrice = (quantity: number): number => {
  const tier = STRIPE_CONFIG.credit_tiers.find(
    tier => quantity >= tier.min && quantity <= tier.max
  );
  return tier?.unit_price || 15.00; // Prix par défaut si hors paliers
};

/**
 * Calcule le prix total pour une quantité de crédits avec économies
 * 
 * @param quantity - Nombre de crédits à acheter
 * @returns Objet contenant le total, le prix unitaire et les économies
 */
export const calculateCreditPrice = (quantity: number): PriceCalculation => {
  const unit_price = getCreditUnitPrice(quantity);
  const total = quantity * unit_price;
  const full_price = quantity * 15.00; // Prix sans remise (palier 1-9)
  const savings = full_price - total;
  
  return { total, unit_price, savings };
};

/**
 * Récupère les informations du palier pour une quantité donnée
 * 
 * @param quantity - Nombre de crédits
 * @returns Informations du palier ou undefined
 */
export const getCreditTierInfo = (quantity: number): CreditTier | undefined => {
  return STRIPE_CONFIG.credit_tiers.find(
    tier => quantity >= tier.min && quantity <= tier.max
  );
};

/**
 * Trouve le pack de crédits prédéfini correspondant à une quantité exacte
 * 
 * @param credits - Nombre de crédits exact
 * @returns Pack de crédits ou undefined si pas de correspondance exacte
 */
export const getCreditPackByAmount = (credits: number): CreditPack | undefined => {
  return STRIPE_CONFIG.credit_packs.find(pack => pack.credits === credits);
};

/**
 * Récupère le price_id Stripe pour une quantité de crédits
 * Si un pack prédéfini existe, retourne son price_id
 * Sinon, retourne le price_id du produit générique
 * 
 * @param credits - Nombre de crédits
 * @returns ID du prix Stripe
 */
export const getPriceIdForCredits = (credits: number): string => {
  const pack = getCreditPackByAmount(credits);
  return pack?.price_id || STRIPE_CONFIG.credits_product.price_id;
};

/**
 * Récupère le product_id Stripe pour une quantité de crédits
 * 
 * @param credits - Nombre de crédits
 * @returns ID du produit Stripe
 */
export const getProductIdForCredits = (credits: number): string => {
  const pack = getCreditPackByAmount(credits);
  return pack?.product_id || STRIPE_CONFIG.credits_product.product_id;
};

/**
 * Calcule le coût mensuel total des abonnements
 * 
 * @param brandingPro - Si l'option Branding Pro est activée
 * @param additionalSeats - Nombre de sièges supplémentaires
 * @returns Coût mensuel total en euros
 */
export const calculateMonthlyCost = (brandingPro: boolean, additionalSeats: number): number => {
  let total = 0;
  
  if (brandingPro) {
    total += STRIPE_CONFIG.branding_pro.price;
  }
  
  total += additionalSeats * STRIPE_CONFIG.seat_additional.price;
  
  return total;
};

/**
 * Récupère tous les packs de crédits prédéfinis triés par quantité
 * 
 * @returns Liste des packs de crédits
 */
export const getAllCreditPacks = (): CreditPack[] => {
  return [...STRIPE_CONFIG.credit_packs].sort((a, b) => a.credits - b.credits);
};

/**
 * Récupère les packs de crédits recommandés pour l'affichage dans l'UI
 * Par défaut: 5, 10, 25, 50, 100, 200 crédits
 * 
 * @returns Liste des packs recommandés
 */
export const getRecommendedCreditPacks = (): CreditPack[] => {
  const recommendedAmounts = [5, 10, 25, 50, 100, 200];
  return STRIPE_CONFIG.credit_packs
    .filter(pack => recommendedAmounts.includes(pack.credits))
    .sort((a, b) => a.credits - b.credits);
};

// ============================================================================
// VARIABLES D'ENVIRONNEMENT
// ============================================================================

export const STRIPE_PUBLISHABLE_KEY = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;
export const STRIPE_SUCCESS_URL = `${window.location.origin}/subscription?success=true`;
export const STRIPE_CANCEL_URL = `${window.location.origin}/subscription`;